import { camelize } from 'humps';
import isEmpty from 'lodash.isempty';
import { formatDateWeekdayDateTimeWithZone } from '../../../utils/formatDate';

// ============================================================================
// Question Helpers
// ----------------------------------------------------------------------------
// ============================================================================
// Prepare Questions
// Filter Hidden Questions
// Regroup questions by their category
// From: [{},{},{}]
// To: { categoryA: [{}], categoryB: [{},{}] }
export const prepareQuestions = ({
  questions,
  hidden,
  features,
  context,
  data = {},
  isSubmission = false,
}) =>
  Object.keys(questions).reduce(
    (prepared, current) => {
      const question = questions[current];
      // Question is NOT Hidden
      // OR
      // Question was answered
      if (!hidden[question.name] || !isEmpty(questionValue(question, data))) {
        // NOT a Submission
        // OR
        // Submission AND show empty answers
        // OR
        // Submission AND NOT show empty answers and question was answered
        if (
          !isSubmission ||
          (isSubmission &&
            (features.showEmptyAnswers ||
              (!features.showEmptyAnswers &&
                !isEmpty(questionValue(question, data)))))
        ) {
          // Group By Category
          features.categories
            ? (
                prepared[question.category] ||
                (prepared[question.category] = {
                  label:
                    (context?.categoryLabels || {})[
                      camelize(question.category)
                    ] || question.category,
                  description: question.config?.categoryDescription,
                  subCategory: question.config?.subCategory,
                  position: question.config?.descriptionPosition,
                  tooltip: question.config?.tooltip,
                  questions: [],
                })
              ).questions.push(question)
            : // OR
              // NOT grouped by category
              prepared.push(question);
        }
      }
      return prepared;
    },
    features.categories ? {} : []
  );

// Array of question names that are required
// { 'q1': true, 'q2': false, 'q3': true} => ['q1', 'q3']
export const requiredQuestionNames = ({ required, hidden }) =>
  Object.keys(required)
    .filter((name) => hidden[name] !== true)
    .reduce((names, name) => {
      if (required[name]) names.push(name);
      return names;
    }, []);

// Prepare Submission Values before sending to the backend
// At runtime, fields can become required that are not known to be required to the backend.
// The backend validates the keys that it's given. If a key is present, the backend validates it is well-formed and valid value.
// Omission of the key will allow the backend to ignore the validation
export const prepareSubmissionValues = ({ required, hidden, values }) =>
  Object.keys(values)
    .filter((name) => {
      const requiredQuestions = requiredQuestionNames({ required, hidden });
      return (
        // Include just Visible questions
        !hidden[name] &&
        // Include Required Questions
        (requiredQuestions.includes(name) ||
          // Include Non-required Questions with a value
          (!requiredQuestions.includes(name) &&
            (!isEmpty(values[name]) || !!values[name] === values[name])))
      );
    })
    .reduce(
      (obj, name) => ({
        ...obj,
        [name]: values[name],
      }),
      {}
    );

// Calculate how much progress has been made in the form.
export const getQuestionsTotal = ({ required, hidden }) =>
  requiredQuestionNames({ required, hidden }).length;

export const getQuestionsCompleted = ({
  required,
  hidden,
  initialValues,
  values,
  questionsTotal,
  isNew,
  dirty,
}) =>
  questionsTotal -
  requiredQuestionNames({ required, hidden }).reduce((count, name) => {
    isEmpty(values[name]) && count++;

    return count;
  }, 0);

/**
 * @param {object} question
 * @returns {string} "question_name" to "questionName"
 */
export const questionName = (question) => camelize(question.name);

/**
 *
 * @param {object} question
 * @param {object} data Submission data values
 */
export const questionValue = (question, data) => data[question.name] || '';

/**
 * @param {string} category
 * @returns {string} "category_label" to "Category Label"
 */
export const prepareCategoryLabel = (category = null) =>
  category && category.replace(/_/g, ' ');

/**
 * Find consult by id from array
 * @param {array} consults
 * @param {string|integer} id 1|"1"
 * @returns {object}
 */
export const getConsult = (consults, id) =>
  consults.find((consult) => consult.id == id);

/**
 * @param {Object[{Object} consult]} consult
 * @param {Object[{Boolean} showId]} showId
 * @returns {String}
 * @example "Pizza Party on 1/1/1999"
 * @example "#abc123—Pizza Party on 1/1/1999"
 */
export const consultLabel = ({ consult, showId = false }) =>
  (consult &&
    [
      `${consult.sessionThemeName ||
        'Session'} on ${formatDateWeekdayDateTimeWithZone(
        consult.scheduledDate
      )}`,
      showId ? `(#${consult.id})` : '',
    ]
      .join(' ')
      .trim()) ||
  '';

/**
 * Consult Option for ReactSelect
 * @param {Object[{Object} consult]}
 * @param {Object[{Boolean} showId]}
 * @returns {Object|Null}
 */
export const consultOption = ({ consult, showId = false }) =>
  consult && createOption(consult.id, consultLabel({ consult, showId }));

/**
 * @param {array} options [{label, value},...]|[{options:[{label, value}, ...]}]
 * @returns {object}
 */
export const optionsMap = (options) => {
  let map = {};
  options.forEach((option) => {
    if (option.options) {
      map = { ...map, ...optionsMap(option.options) };
    }
    if (option.value) {
      map[option.value] = option;
    }
  });
  return map;
};

/**
 * Retrieved object reference for value to populate ReactSelect
 * @param {string} value
 * @param {object} options
 * @param {boolean} multiple
 * @returns {array|object|string} [{Option Ref}, {Option Ref}, ...] | {Option Ref} | ""
 */
export const mapValueToOptions = (value, options, multiple) => {
  // Map { "value" : { Option Ref } }
  const opts = optionsMap(options);
  return (
    (value &&
      (multiple && Array.isArray(value)
        ? value.map((v) => opts[v])
        : opts[value])) ||
    ''
  );
};

/**
 * Create Option Object for ReactSelect
 * @param {string} value
 * @param {string} label
 * @returns {object}
 */
export const createOption = (value, label = null) => ({
  value: value,
  label: label || value,
});

/**
 * Search two levels of options for a value and return corresponding label
 * Fallback to value || Fallback to empty string (Formik)
 * @param {array} options
 * @param {string} value
 * @returns {string}
 */
export const getOptionLabel = (options = [], value) => {
  const option = optionsMap(options)[value];
  return (option && option.label) || value || '';
};

/**
 * Progress label for required questions
 * @param {array} urls
 * @param {integer} current
 * @returns {string} "1 of 10"
 */
export const progressLabel = (urls, current) =>
  `${1 + current} of ${urls.length}`;

/**
 * "No" Boolean Helper
 * Our Bools are Strings (:
 * @param {string} value
 * @returns {boolean}
 */
export const isNoBoolean = (value) => value === 'No';

/**
 * "Yes" Boolean Helper
 * Our Bools are Strings (:
 * @param {string} value
 * @returns {boolean}
 */
export const isYesBoolean = (value) => value === 'Yes';
